import React, {useEffect, useMemo, useState} from 'react';

import {useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import OtpInput from 'react-otp-input';

import {LanguageSelector} from '@/widgets/LanguageSelector';
import {ROUTES} from '@app/routing';
import {colors} from '@app/styles/theme';
import {userModel} from '@entities/user/model';
import {history} from '@shared/lib/history';
import {Button, ButtonVariant} from '@shared/ui/Button';

import {
    ControlOTPContainer,
    FormDescription,
    LanguageWrapper,
    MainContainer,
    OptWrapper,
    PasswordContent,
    PasswordTitle,
    ServiceTitle,
    Timer,
} from './styled';

export function OTPForm() {
    const {t} = useTranslation();
    const [otp, setOtp] = useState('');
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState(120);
    const [autoConfirm, setAutoConfirm] = useState(false);

    const user = useUnit(userModel);
    const email = useMemo(() => user.state.profile?.email, [user]);

    useEffect(() => {
        autoConfirm && otp.length === 6 && onConfirm();
    }, [otp]);

    useEffect(() => {
        if (!user.state.authData) history.push(ROUTES.LOGIN);
    }, [user.state.authData]);

    useEffect(() => {
        if (timeLeft === 0) return;
        const timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
        return () => clearInterval(timer);
    }, [timeLeft]);

    const onConfirm = async () => {
        if (otp.length === 6 && sessionId) {
            setAutoConfirm(false);
            await user.verifyOtp({code: otp, email, sessionId});
        }
    };

    const onResend = async () => {
        setAutoConfirm(true);
        setOtp('');
        if (!email) return;

        try {
            const id = (await user.sendOtp({email, userName: email})).data;
            setSessionId(id);
            setTimeLeft(120);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        onResend();
    }, [user.state.profile]);

    return (
        <>
            <div>
                <ServiceTitle size={4}>traceCORE B2B E-Invoicing</ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <PasswordContent>
                    <PasswordTitle size={3}>{t('otpVerify.title')}</PasswordTitle>
                    <FormDescription>
                        {t('otpVerify.description')} <span style={{fontWeight: 700}}>{email}</span>
                    </FormDescription>

                    <OptWrapper>
                        <OtpInput
                            value={otp}
                            onChange={setOtp}
                            numInputs={6}
                            renderInput={(props) => <input {...props} />}
                            renderSeparator={
                                <span
                                    style={{
                                        color: user.state.error.otp
                                            ? colors.danger
                                            : colors.textMuted,
                                    }}
                                >
                                    —
                                </span>
                            }
                            inputStyle={{
                                width: '48px',
                                height: '42px',
                                margin: '0 8px',
                                fontSize: '24px',
                                borderRadius: '6px',
                                outline: 'none',
                                border: `1px solid ${
                                    user.state.error.otp ? colors.danger : colors.textMuted
                                }`,
                            }}
                            inputType="tel"
                            shouldAutoFocus
                        />
                    </OptWrapper>

                    <Timer>
                        {timeLeft > 0 ? (
                            <span>
                                {t('otpVerify.timer', {
                                    min: Math.floor(timeLeft / 60),
                                    sec: (timeLeft % 60).toString().padStart(2, '0'),
                                })}
                            </span>
                        ) : (
                            <span style={{color: colors.danger}}>{t('otpVerify.expired')}</span>
                        )}
                    </Timer>

                    <ControlOTPContainer>
                        <Button
                            variant={ButtonVariant.Outline}
                            onClick={onResend}
                            isLoading={user.state.pending}
                            disabled={timeLeft > 0}
                        >
                            {t('otpVerify.resendButton')}
                        </Button>

                        <Button
                            onClick={onConfirm}
                            isLoading={user.state.pending}
                            disabled={otp.length < 6 || timeLeft === 0}
                        >
                            {t('otpVerify.confirmButton')}
                        </Button>

                        <Button
                            style={{gridColumn: '1 / -1'}}
                            variant={ButtonVariant.Text}
                            onClick={() => user.resetAuth()}
                        >
                            {t('otpVerify.returnButton')}
                        </Button>
                    </ControlOTPContainer>
                </PasswordContent>
            </MainContainer>
        </>
    );
}
